import { useSnackbarContext } from '@/contexts/SnackbarContext';
import FieldTitle from '@superfunnel/ui-core/v2/FieldTitle';
import Input, { InputProps } from '@superfunnel/ui-core/v2/Input';
import { useCallback, useEffect, useState } from 'react';
import { useDynamicPageSettingsContext } from './DynamicPageSettingsContext';
import { DynamicPageSettingsModalTabWrapper } from './DynamicPageSettingsModalTabWrapper';

const MIN_NAME_LENGTH = 2;
const MAX_NAME_LENGTH = 120;
const DEFAULT_AUTO_ADVANCE_DELAY = 300;
const MIN_AUTO_ADVANCE_DELAY = 0;
const MAX_AUTO_ADVANCE_DELAY = 2000;

export const QuizOptions = () => {
  const { show } = useSnackbarContext();
  const { quizOptions } = useDynamicPageSettingsContext();

  // Local state for form fields
  const [localName, setLocalName] = useState(quizOptions?.formName ?? '');
  const [localAutoAdvanceDelay, setLocalAutoAdvanceDelay] = useState(
    quizOptions?.autoAdvanceDelay ?? DEFAULT_AUTO_ADVANCE_DELAY,
  );
  const [edit, setEdit] = useState(false);

  // Sync local state with prop changes (when modal opens with new data)
  useEffect(() => {
    setLocalName(quizOptions?.formName ?? '');
    setLocalAutoAdvanceDelay(
      quizOptions?.autoAdvanceDelay ?? DEFAULT_AUTO_ADVANCE_DELAY,
    );
  }, [quizOptions?.formName, quizOptions?.autoAdvanceDelay]);

  // Track if there are unsaved changes
  useEffect(() => {
    const nameChanged = localName !== (quizOptions?.formName ?? '');
    const delayChanged =
      localAutoAdvanceDelay !==
      (quizOptions?.autoAdvanceDelay ?? DEFAULT_AUTO_ADVANCE_DELAY);
    setEdit(nameChanged || delayChanged);
  }, [localName, localAutoAdvanceDelay, quizOptions]);

  const validateName = (name: string): InputProps['validation'] | undefined => {
    const trimmed = name.trim();
    if (trimmed.length < MIN_NAME_LENGTH) {
      return {
        message: `Name must be at least ${MIN_NAME_LENGTH} characters`,
        status: 'error',
      };
    }
    if (trimmed.length > MAX_NAME_LENGTH) {
      return {
        message: `Name must be at most ${MAX_NAME_LENGTH} characters`,
        status: 'error',
      };
    }
    return undefined;
  };

  const onDiscard = useCallback(() => {
    setLocalName(quizOptions?.formName ?? '');
    setLocalAutoAdvanceDelay(
      quizOptions?.autoAdvanceDelay ?? DEFAULT_AUTO_ADVANCE_DELAY,
    );
  }, [quizOptions]);

  const onSave = useCallback(async () => {
    if (!quizOptions?.onSave) return;

    const validationError = validateName(localName);
    if (validationError?.message) {
      show(validationError.message);
      return;
    }

    try {
      await quizOptions.onSave({
        formName: localName.trim(),
        autoAdvanceDelay: localAutoAdvanceDelay,
      });
      show('Updated');
    } catch (err) {
      console.error('Failed to save quiz options:', err);
    }
  }, [quizOptions, localName, localAutoAdvanceDelay, show]);

  if (!quizOptions) {
    return (
      <div className="flex h-full items-center justify-center text-gray-500">
        Quiz options are not available.
      </div>
    );
  }

  return (
    <DynamicPageSettingsModalTabWrapper
      edit={edit}
      onDiscard={onDiscard}
      onSave={onSave}
    >
      {/* Form Name */}
      <Input
        title="Form Name"
        value={localName}
        onChange={(e) => {
          setLocalName(e.target.value);
        }}
        placeholder="Enter form name"
        maxLength={MAX_NAME_LENGTH}
        validation={validateName(localName)}
      />

      {/* Auto-Advance Delay */}
      <div>
        <FieldTitle title="Auto-Advance Delay (ms)" className="mb-1" />
        <Input
          type="number"
          value={localAutoAdvanceDelay}
          onChange={(e) => {
            const value = parseInt(e.target.value, 10);
            if (isNaN(value)) return;
            const clampedValue = Math.max(
              MIN_AUTO_ADVANCE_DELAY,
              Math.min(MAX_AUTO_ADVANCE_DELAY, value),
            );
            setLocalAutoAdvanceDelay(clampedValue);
          }}
          min={MIN_AUTO_ADVANCE_DELAY}
          max={MAX_AUTO_ADVANCE_DELAY}
          placeholder={String(DEFAULT_AUTO_ADVANCE_DELAY)}
        />
        <p className="text-base-500 mt-1 text-xs">
          Time in milliseconds before auto-advancing to the next step (0-2000
          ms)
        </p>
      </div>
    </DynamicPageSettingsModalTabWrapper>
  );
};
